import { useRef, useState } from 'react'
import { toReadable, toTokens } from '@/lib/autocomplete'
import { useAutosize } from '@/lib/useAutosize'
import { coarsePointer } from '@/lib/mobile'
import { describeError } from '@/lib/auth'
import { FormatToolbar } from './FormatToolbar'
import { useFormatToolbar } from './useFormatToolbar'
import styles from './MessageRow.module.css'

type Namer = (kind: 'user' | 'role' | 'channel', id: string) => string | undefined

/**
 * Правка сообщения — то же поле ввода в малом: упоминания читаемые (@Ник, @роль, #канал) и перед
 * сохранением снова становятся токенами; над выделением — панель форматирования, Ctrl/⌘+B, I, U.
 * Раньше правка была голым полем с токенами вместо имён и без разметки (правка №14).
 */
export function MessageEditor({ content, name, onSave, onCancel }: { content: string; name: Namer; onSave: (content: string) => Promise<void>; onCancel: () => void }) {
  // один раз при открытии: имена потом могут догрузиться, а карта должна совпадать с тем, что в поле
  const [initial] = useState(() => toReadable(content, name))
  const [draft, setDraft] = useState(initial.text)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  useAutosize(textareaRef, draft)
  const format = useFormatToolbar(textareaRef, { toolbarRef })
  const savingRef = useRef(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = async () => {
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    setError(null)
    try {
      await onSave(toTokens(draft, initial.picked))
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  return (
    <form className={styles.editor} aria-label="Редактирование сообщения" onSubmit={(e) => { e.preventDefault(); void save() }}>
      <div className={styles.editField}>
        {format.toolbar ? <FormatToolbar ref={toolbarRef} text={draft} {...format.toolbar} /> : null}
        <textarea
          ref={textareaRef}
          className={styles.editInput}
          value={draft}
          readOnly={saving}
          autoFocus
          rows={1}
          aria-label="Текст сообщения"
          onChange={(e) => setDraft(e.target.value)}
          onSelect={format.onSelect}
          onBlur={format.onBlur}
          onPaste={(e) => {
            format.onPaste(e)
          }}
          onFocus={(e) => {
            // курсор — в конец текста, как в Discord: правят чаще конец
            const end = e.currentTarget.value.length
            e.currentTarget.setSelectionRange(end, end)
          }}
          onKeyDown={(e) => {
            if (saving || e.nativeEvent.isComposing) return
            // Esc при открытой панели сначала закрывает её
            if (format.onKeyDown(e)) return
            if (e.key === 'Escape') {
              e.preventDefault()
              onCancel()
            } else if (e.key === 'Enter' && !e.shiftKey && !coarsePointer) {
              e.preventDefault()
              void save()
            }
          }}
        />
      </div>
      {error ? <p className={styles.editError} role="alert">{error}</p> : null}
      <div className={styles.editFooter}>
        <span className={styles.editHint}>Esc — отмена · Enter — сохранить</span>
        <div className={styles.editActions}>
          <button type="button" disabled={saving} onClick={onCancel}>Отмена</button>
          <button type="submit" className={styles.editSave} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</button>
        </div>
      </div>
    </form>
  )
}
